import { useEffect, useRef, useState } from 'react';
import {
  catalogThumbForAssetId,
  loadBookingListAssetThumbnails,
  normalizeAssetLookupKey,
} from '../utils/bookingAssetThumbnailCache';
import { coalesceAssetListRecord } from '../utils/assetListNormalize';
import { resolveBookingAssetThumbnailSrc } from '../utils/bookingListDisplay';
import { assetRecordPrimaryId } from '../utils/assetRecordPrimaryId';

/** Cap concurrent GET-by-id thumbnails for large keyword/browse lists (search rows often omit images). */
export const ASSET_BROWSE_THUMB_FETCH_CAP = 24;

/** Max parallel thumbnail GETs within a capped batch. */
export const ASSET_BROWSE_THUMB_FETCH_CONCURRENCY = 4;

function listRowThumbFromRecord(record: Record<string, unknown>): string | null {
  return resolveBookingAssetThumbnailSrc(coalesceAssetListRecord(record));
}

function sharedEmbeddedImageUrls(records: Record<string, unknown>[]): Set<string> {
  const counts = new Map<string, number>();
  for (const row of records) {
    const id = assetRecordPrimaryId(row);
    const src = listRowThumbFromRecord(row);
    if (!id || !src) {
      continue;
    }
    counts.set(src, (counts.get(src) ?? 0) + 1);
  }
  const shared = new Set<string>();
  counts.forEach((count, src) => {
    if (count > 1) {
      shared.add(src);
    }
  });
  return shared;
}

function lookupKeysForAssetId(assetId: string): string[] {
  return [assetId.toLowerCase(), normalizeAssetLookupKey(assetId)];
}

function buildAssetsContentKey(assets: Record<string, unknown>[]): string {
  try {
    return JSON.stringify(
      assets.map((asset) => {
        const id = assetRecordPrimaryId(asset);
        const embedded = listRowThumbFromRecord(asset);
        const address =
          (typeof asset.address === 'string' && asset.address.trim()) ||
          (typeof asset.city === 'string' && asset.city.trim()) ||
          '';
        return [id, embedded, address || asset.asset_name || asset.name || ''].join('\0');
      }),
    );
  } catch {
    return String(assets.length);
  }
}

function assetIdsNeedingThumbnailFetch(
  records: Record<string, unknown>[],
  sharedEmbeddedUrls: ReadonlySet<string>,
  skipKeys: ReadonlySet<string>,
): string[] {
  const out: string[] = [];
  const seen = new Set<string>();
  for (const row of records) {
    const id = assetRecordPrimaryId(row);
    if (!id) {
      continue;
    }
    const keys = lookupKeysForAssetId(id);
    if (keys.some((key) => seen.has(key) || skipKeys.has(key))) {
      continue;
    }
    const embedded = listRowThumbFromRecord(row);
    if (embedded && !sharedEmbeddedUrls.has(embedded)) {
      continue;
    }
    for (const key of keys) {
      seen.add(key);
    }
    out.push(id);
  }
  return out;
}

/** Fetches missing list-card thumbnails via GET asset by id (search rows often omit per-asset images). */
export function useAssetBrowseThumbs(
  assets: Record<string, unknown>[],
): Map<string, string | null> {
  const [thumbByAssetId, setThumbByAssetId] = useState<Map<string, string | null>>(() => new Map());
  const trustedKeysRef = useRef<Set<string>>(new Set());
  const inFlightKeysRef = useRef<Set<string>>(new Set());
  const assetsKey = buildAssetsContentKey(assets);

  useEffect(() => {
    trustedKeysRef.current.clear();
    const sharedEmbeddedUrls = sharedEmbeddedImageUrls(assets);
    setThumbByAssetId((prev) => {
      let changed = false;
      const next = new Map(prev);
      for (const row of assets) {
        const id = assetRecordPrimaryId(row);
        if (!id) {
          continue;
        }
        const embedded = listRowThumbFromRecord(row);
        if (!embedded || sharedEmbeddedUrls.has(embedded)) {
          continue;
        }
        for (const key of lookupKeysForAssetId(id)) {
          if (next.get(key) !== embedded) {
            next.set(key, embedded);
            changed = true;
          }
          trustedKeysRef.current.add(key);
        }
      }
      return changed ? next : prev;
    });
  }, [assets, assetsKey]);

  useEffect(() => {
    const sharedEmbeddedUrls = sharedEmbeddedImageUrls(assets);
    const skipKeys = new Set<string>();
    trustedKeysRef.current.forEach((key) => skipKeys.add(key));
    inFlightKeysRef.current.forEach((key) => skipKeys.add(key));
    const needed = assetIdsNeedingThumbnailFetch(assets, sharedEmbeddedUrls, skipKeys).slice(
      0,
      ASSET_BROWSE_THUMB_FETCH_CAP,
    );
    if (needed.length === 0) {
      return undefined;
    }

    for (const assetId of needed) {
      for (const key of lookupKeysForAssetId(assetId)) {
        inFlightKeysRef.current.add(key);
      }
    }

    let cancelled = false;
    const listSnapshots = new Map<string, Record<string, unknown>>();
    for (const row of assets) {
      const id = assetRecordPrimaryId(row);
      if (id) {
        listSnapshots.set(id, row);
      }
    }
    void (async () => {
      const loaded = await loadBookingListAssetThumbnails(needed, {
        forceRefresh: true,
        listSnapshots,
        concurrency: ASSET_BROWSE_THUMB_FETCH_CONCURRENCY,
      });
      if (cancelled) {
        for (const assetId of needed) {
          for (const key of lookupKeysForAssetId(assetId)) {
            inFlightKeysRef.current.delete(key);
          }
        }
        return;
      }
      setThumbByAssetId((prev) => {
        let changed = false;
        const next = new Map(prev);
        for (const assetId of needed) {
          const src = catalogThumbForAssetId(loaded, assetId);
          const keys = lookupKeysForAssetId(assetId);
          for (const key of keys) {
            inFlightKeysRef.current.delete(key);
          }
          if (!src) {
            continue;
          }
          for (const key of keys) {
            if (next.get(key) !== src) {
              next.set(key, src);
              changed = true;
            }
            trustedKeysRef.current.add(key);
          }
        }
        return changed ? next : prev;
      });
    })();

    return () => {
      cancelled = true;
      for (const assetId of needed) {
        for (const key of lookupKeysForAssetId(assetId)) {
          inFlightKeysRef.current.delete(key);
        }
      }
    };
  }, [assets, assetsKey]);

  return thumbByAssetId;
}
